import {
  NetworkAssessmentAnalysisType,
  RegionSetStatusType,
} from "@app/projects/enumerators";
import { isEqual } from "lodash";
import { Component } from "react";
import { AdequacyFilters } from "../../projects/network-assessment/adequacy";
import {
  ExportFormatType,
  ExportGapFilterType,
  ReportSettingsType,
} from "../enumerations";
import {
  RegionSetFilter,
  ReportSettingsModel,
  SpecialtyFilter,
} from "../exportModels";
import * as helpers from "./exportPanelBodyHelpers";
import {
  SelectFormat,
  SelectRegionSetFilter,
  SelectSpecialtyFilter,
} from "./filters";
import GenerateBody from "./generate-body";
import {
  ReportSettingsBody,
  ReportSettingsSaveAction,
} from "./report-settings";
import {
  ReportSettingsProps,
  ReportSettingsState,
} from "./report-settings/reportSettings";
import {
  ReportPanelBodyBaseState,
} from "./reportPanelBodyBase";
import { ReportPanelBodyBaseProps } from "./reportPanelBodyBaseProps";
import * as toast from "./toast";

interface CountyAggregatePanelBodyProps
  extends ReportPanelBodyBaseProps,
    ReportSettingsProps {
  adequacyFilters: AdequacyFilters;
  projectId: number;
  runConfigurationId: number;
  showExpandableSpecialtiesOption: boolean;
  specialtyGroupCodes: string[];
}

interface CountyAggregatePanelBodyState
  extends ReportPanelBodyBaseState,
    ReportSettingsState {
  regionSetFilter: RegionSetFilter;
  sharedUsers: number[];
  specialtyFilter: SpecialtyFilter;
}

export default class CountyAggregatePanelBody extends Component<
  CountyAggregatePanelBodyProps,
  CountyAggregatePanelBodyState
> {
  private readonly testIdSuffix = "county-aggregate-panel";

  constructor(props: CountyAggregatePanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
      ...this.getInitialReportSettingsState(),
    };
  }

  public componentDidUpdate(prevProps: CountyAggregatePanelBodyProps) {
    if (!isEqual(prevProps.reportSettingsList, this.props.reportSettingsList)) {
      const reportSettingsList = this.props.reportSettingsList;
      if (reportSettingsList) {
        const currentReportSettings = reportSettingsList.find(
          (rs) => rs.isDefault
        );
        if (currentReportSettings)
          this.handleParseReportSettingsJsonToState(
            currentReportSettings.reportSettingsJson
          );
        this.setState({
          currentReportSettings,
          reportSettingsList,
        });
      }
    }
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {

    return (
      <>
        {
          <ReportSettingsBody
            buildReportSettingsModel={this.buildReportSettingsModel}
            createReportSettingsNameInputDisabled={
              this.state.createReportSettingsNameInputDisabled
            }
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            parseReportSettingsJsonToState={
              this.handleParseReportSettingsJsonToState
            }
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={
              this.state.reportSettingsHasInvalidChanges
            }
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCreateReportSettingsNameInputDisabled={
              this.setCreateReportSettingsNameInputDisabled
            }
            setCurrentReportSettings={this.setCurrentReportSettings}
            setReportSettingsIsSelectionValid={
              this.setReportSettingsIsSelectionValid
            }
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsHasInvalidChanges={
              this.setReportSettingsHasInvalidChanges
            }
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
          />
        }
        <div className="row">
          <div className="col-12 col-sm-4">
            <SelectSpecialtyFilter
              disabled={this.props.disableSpecialtyGroupFilters}
              onExpandableSpecialtyIncludeChange={
                this.handleExpandableSpecialtyIncludeChange
              }
              onSpecialtyGapFilterChange={this.handleSpecialtyGapFilterChange}
              onSpecialtyIncludeChange={this.handleSpecialtyIncludeChange}
              showExpandableSpecialtyOption={
                this.props.showExpandableSpecialtiesOption
              }
              specialtyFilter={this.state.specialtyFilter}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectRegionSetFilter
              disabled={this.props.disableRegionFilters}
              onNetworkAssessmentAnalysisTypeChange={
                this.handleNetworkAssessmentAnalysisTypeChange
              }
              onRegionSetFilterTypeChange={this.handleRegionSetFilterTypeChange}
              onRegionSetIncludeChange={this.handleRegionSetIncludeChange}
              regionSetFilter={this.state.regionSetFilter}
              singularDisplay={this.props.singularDisplay}
              pluralDisplay={this.props.pluralDisplay}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectFormat
              outputFormat={this.state.outputFormat}
              onOutputFormatChange={this.handleOutputFormatChange}
              excelDisabled={false}
              exportType="adequacyBulkAggregateReport"
            />
          </div>
        </div>
        {
          <ReportSettingsSaveAction
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={
              this.state.reportSettingsHasInvalidChanges
            }
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCurrentReportSettings={this.setCurrentReportSettings}
            setCreateReportSettingsNameInputDisabled={
              this.setCreateReportSettingsNameInputDisabled
            }
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
            stringifyStateReportSettings={this.stringifyStateReportSettings}
          />
        }
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={
            this.state.loadingReportSettingsJSONStrings &&
            this.state.loadingReportSettingsJSONStrings.indexOf(
              helpers.stringifyStateReportSettings(this.state)
            ) > -1
          }
          isValid={
            this.state.regionSetFilter.includeActive ||
            this.state.regionSetFilter.includePending ||
            this.state.regionSetFilter.includeApproved
          }
          showSharedUsers={this.props.match.params.module === "adequacy"}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.updateSharedUsers}
          exportType="AdequacyBulkAggregateReport"
          projectId={this.props.projectId}
          networkAssessmentAnalysisType={
            this.state.regionSetFilter.networkAssessmentAnalysisType
          }
        />
      </>
    );
  }

  private getInitialBodyState = (): CountyAggregatePanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      outputFormat: ExportFormatType.Excel,
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType:
          this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName(
        this.props.comparedRunConfigurationId !== null
          ? "Adequacy Aggregate Bulk"
          : "Aggregation"
      ),
      sharedUsers: [],
      specialtyFilter: {
        filterType: ExportGapFilterType.All,
        includeExpandableSpecialties: false,
        includeFacilitySpecialties: true,
        includePhysicianSpecialties: true,
      },
    } as CountyAggregatePanelBodyState;
  };

  private getInitialReportSettingsState = (): ReportSettingsState => {
    return {
      createReportSettingsNameInputDisabled: true,
      currentReportSettings: undefined,
      isSelectionValid: true,
      reportSettingsHasChanges: false,
      reportSettingsHasInvalidChanges: false,
      reportSettingsList: [],
      saveClicked: false,
    } as ReportSettingsState;
  };

  private handleRegionSetFilterTypeChange = (
    filterType: ExportGapFilterType
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        filterType,
      },
      reportSettingsHasChanges: true,
    }));
  };

  private handleNetworkAssessmentAnalysisTypeChange = (
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        networkAssessmentAnalysisType,
      },
      reportSettingsHasChanges: true,
    }));
  };

  private handleRegionSetIncludeChange = (
    selected: boolean,
    regionSetStatus: RegionSetStatusType
  ) => {
    let activeSelected: boolean = this.state.regionSetFilter.includeActive;
    let approvedSelected: boolean = this.state.regionSetFilter.includeApproved;
    let pendingSelected: boolean = this.state.regionSetFilter.includePending;

    switch (regionSetStatus) {
      case RegionSetStatusType.Active:
        activeSelected = selected;
        break;
      case RegionSetStatusType.Approved:
        approvedSelected = selected;
        break;
      case RegionSetStatusType.Pending:
        pendingSelected = selected;
        break;
    }

    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        includeActive: activeSelected,
        includeApproved: approvedSelected,
        includePending: pendingSelected,
      },
      reportSettingsHasChanges: true,
    }));
  };

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat, reportSettingsHasChanges: true });
  };

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(
      this.state
    );
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    toast.toastInfo(this.state.reportName);
  };

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess, reportSettingsHasChanges: true });
  };

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  };

  private updateSharedUsers = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  };

  private handleExpandableSpecialtyIncludeChange = (selected: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeExpandableSpecialties: selected,
      },
    }));
  };

  private handleSpecialtyIncludeChange = (
    selected: boolean,
    isPhysician: boolean
  ) => {
    let physiciansSelected: boolean =
      this.state.specialtyFilter.includePhysicianSpecialties;
    let facilitiesSelected: boolean =
      this.state.specialtyFilter.includeFacilitySpecialties;

    if (isPhysician) {
      physiciansSelected = selected;
    } else {
      facilitiesSelected = selected;
    }

    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeFacilitySpecialties: facilitiesSelected,
        includePhysicianSpecialties: physiciansSelected,
      },
    }));
  };

  private handleSpecialtyGapFilterChange = (
    filterType: ExportGapFilterType
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        filterType,
      },
    }));
  };

  // Report Settings
  private stringifyStateReportSettings = (): string => {
    return helpers.stringifyStateReportSettings({
      emailOnSuccess: this.state.emailOnSuccess,
      loadingReportSettingsJSONStrings:
        this.state.loadingReportSettingsJSONStrings,
      outputFormat: this.state.outputFormat,
      regionSetFilter: this.state.regionSetFilter,
      specialtyFilter: this.state.specialtyFilter,
    } as CountyAggregatePanelBodyState);
  };

  private buildReportSettingsModel = (
    isDefault: boolean,
    name: string
  ): ReportSettingsModel => {
    const reportSettingsJson = this.stringifyStateReportSettings();
    return {
      isDefault,
      name,
      reportSettingsJson,
      type: this.reportSettingsType,
    } as ReportSettingsModel;
  };

  private get reportSettingsType() {
    switch (this.props.match.params.module) {
      case "accuracy":
        return ReportSettingsType.AdequacyCountyAggregate;
      default:
        return ReportSettingsType.Unknown;
    }
  }

  private handleParseReportSettingsJsonToState = (
    reportSettingsJson: string
  ) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);
    if (settings) {
      this.setReportSettingsIsSelectionValid(true);
      this.setState((prevState) => ({
        ...prevState,
        ...settings,
      }));
    } else {
      const initialBodyState = this.getInitialBodyState();
      this.setReportSettingsIsSelectionValid(false);
      this.setState((prevState) => ({
        ...prevState,
        ...initialBodyState,
      }));
    }
  };

  private setCurrentReportSettings = (
    currentReportSettings: ReportSettingsModel
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      currentReportSettings,
    }));
  };

  private setReportSettingsList = (rsList: ReportSettingsModel[]) => {
    const reportSettingsList = this.props.sortedReportSettings(rsList, "name");
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsList,
    }));
  };

  private setReportSettingsHasChanges = (reportSettingsHasChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges,
    }));
  };

  private setReportSettingsHasInvalidChanges = (
    reportSettingsHasInvalidChanges: boolean
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasInvalidChanges,
    }));
  };

  private setReportSettingsIsSelectionValid = (isSelectionValid: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isSelectionValid,
    }));
  };

  private setCreateReportSettingsNameInputDisabled = (
    createReportSettingsNameInputDisabled: boolean
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      createReportSettingsNameInputDisabled,
    }));
  };

  private setSaveClicked = (saveClicked: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      saveClicked,
    }));
  };
}
